import type { ReactFormExtendedApi, Validator } from '@tanstack/react-form';
import type { ClassValue } from 'clsx';
import type { z } from 'zod';

import { toCamelCase } from '@connected/api-client/lib/utils';
import { useDebounce } from '@connected/ui/hooks/use-debounce';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

type UseUrlSearchSyncOptions = {
    /** The initial search value from the URL */
    search: string | undefined;
    /** Callback to navigate/update the URL with the new search value */
    navigate: (search: string | undefined) => void;
    /** Debounce delay in milliseconds (default: 300) */
    debounceMs?: number;
};

/**
 * A custom hook that synchronizes a search input field state with URL navigation.
 *
 * This hook manages a local search field state and automatically syncs it with
 * the URL through the provided navigate callback. When the search field changes,
 * it triggers navigation with the updated search value after debouncing.
 *
 * @param options - Configuration options for the hook
 * @param options.search - The initial search value from the URL query params
 * @param options.navigate - Callback function to update the URL with the new search value
 * @param options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns A tuple containing the current search field value and its setter function
 *
 * @example
 * ```tsx
 * const [searchField, setSearchField] = useUrlSearchSync({
 *   search: urlSearchParams.search,
 *   navigate: (newSearch) => router.push({ search: { search: newSearch } }),
 *   debounceMs: 500
 * });
 * ```
 */
export function useUrlSearchSync({ search, navigate, debounceMs = 300 }: UseUrlSearchSyncOptions) {
    const [searchState, setSearchState] = useState<string>(search ?? '');
    const debouncedSearch = useDebounce(searchState, debounceMs);

    useEffect(() => navigate(debouncedSearch ? debouncedSearch === '' ? undefined : debouncedSearch : undefined), [debouncedSearch, navigate]);

    return [
        searchState,
        setSearchState,
    ] as const;
}

export const DECIMAL_PREFIXES = [
    'B',
    'kB',
    'MB',
    'GB',
    'TB',
    'PB',
    'EB',
    'ZB',
    'YB',
] as const;
export type DecimalPrefix = (typeof DECIMAL_PREFIXES)[number];
export const BINARY_PREFIXES = [
    'B',
    'KiB',
    'MiB',
    'GiB',
    'TiB',
    'PiB',
    'EiB',
    'ZiB',
    'YiB',
] as const;
export type BinaryPrefix = (typeof BINARY_PREFIXES)[number];

/**
 * A utility function to format a number as a human-readable file size.
 * @param size The size of the file in bytes.
 * @returns The formatted file size.
 */
export function humanFileSize(
    size: number,
    config: {
        base: 1000 | 1024;
        binary: boolean;
        round: number;
    } = { base: 1000, binary: false, round: 2 },
) {
    const i
        = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(config.base));
    const value = size / config.base ** i;

    const prefix = config.binary ? BINARY_PREFIXES : DECIMAL_PREFIXES;

    return `${value.toFixed(config.round)} ${prefix[i]}`;
}

/**
 * A utility function to merge Tailwind CSS classes with the clsx utility.
 * @param inputs The classes to merge.
 * @returns The merged classes.
 */
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * A utility function to format a file name.
 * @param file The file to format.
 * @returns The formatted file name.
 */
export function formatFileName(file: File) {
    const array = file.name.split('.');
    const extension = array.pop();
    const name = array.join('.');

    if (name.length > 20)
        return `${name.slice(0, 20)}...${extension}`;

    return `${name}.${extension}`;
}

export function blobToFile(blob: Blob, fileName: string, fileType?: string) {
    const file = new File([blob], fileName, { type: fileType });

    return file;
}

export async function wait(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function booleanDefaultValue(
    value: boolean | null | undefined,
): boolean | null {
    return typeof value == 'boolean' ? value : null;
}

type FormError<T> = {
    _fieldName: keyof T | null;
    _errorMessage: string;
};

export function getFormErrors<T>(error: unknown): FormError<T>[] {
    const formErrors: FormError<T>[] = [];
    // Als dit voorkomt, dan is alles volledig fucked, zou nooit moeten gebeuren
    if (typeof error !== 'object' || error === null) {
        formErrors.push({
            _fieldName: null,
            _errorMessage: 'Een onbekende fout heeft zich voorgedaan (1)',
        });
        return formErrors;
    }

    // In dit geval ligt de error niet aan een van de velden, maar aan de API zelf
    if (!('ModelState' in error)) {
        formErrors.push({
            _fieldName: null,
            _errorMessage: 'Een onbekende fout heeft zich voorgedaan (2)',
        });
        return formErrors;
    }

    // In dit geval is het er een error met een veld, maar niet het veld wat we insturen
    const errorWithModelState = error as { ModelState: Record<string, unknown> };
    Object.entries(errorWithModelState.ModelState).forEach(([key, value]) => {
        // In deze gevallen heeft de achterkant geen error die wij kunnen parsen
        if (!Array.isArray(value))
            return 'Een onbekende fout heeft zich voorgedaan (4)';
        if (value.length === 0)
            return 'Een onbekende fout heeft zich voorgedaan (5)';
        formErrors.push({
            _fieldName: toCamelCase(key.replace('command.', '') as keyof T),
            _errorMessage: value.join(', '),
        });
    });

    return formErrors;
}

type FormErrorProps<T> = {
    fieldErrors: FormError<T>[];
    formApi: ReactFormExtendedApi<T, Validator<unknown, z.ZodType<any, z.ZodTypeDef, any>>>;
};

export function placeFormErrors<T>({ fieldErrors, formApi }: FormErrorProps<T>) {
    fieldErrors.forEach(({ _fieldName, _errorMessage }) => {
        if (_fieldName === null) {
            formApi.setErrorMap({
                onSubmit: _errorMessage,
            });
            return;
        }

        try {
            formApi.fieldInfo[_fieldName as keyof typeof formApi.fieldInfo]?.instance?.setErrorMap({
                onSubmit: _errorMessage,
            });
        }
        catch (error) {
            console.error(`Error setting error for field ${String(_fieldName)}:`, error);
        }
    });
}
